import { Button, Card, Input, Select } from "components"
import type { Dispatch, SetStateAction } from "react"
import type { ListItemSummaryFilter } from "../types/listItemSummary"
import {
  CATEGORY_OPTIONS,
  LOCATION_OPTIONS,
  MOVEMENT_OPTIONS,
  RANGE_MODE_OPTIONS,
  STOCK_STATUS_OPTIONS,
  SUPPLIER_OPTIONS,
  VALUE_RANGE_OPTIONS
} from "../types/listItemSummary"

type FilterSectionProps = {
  filter: ListItemSummaryFilter
  setFilter: Dispatch<SetStateAction<ListItemSummaryFilter>>
  onSearchReport: () => void
  onResetFilter: () => void
  onLatestItems: () => void
}

type Option = { value: string; label: string }

function optionByValue(
  options: ReadonlyArray<Option>,
  value: string
): Option | null {
  return options.find((o) => o.value === value) ?? null
}

export function FilterSection({
  filter,
  setFilter,
  onSearchReport,
  onResetFilter,
  onLatestItems
}: FilterSectionProps) {
  return (
    <Card className="shadow-sm">
      <div className="flex flex-col gap-1 border-b border-[#dadada] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wide text-base-content">
          Report Filter
        </h3>
        <p className="text-xs text-base-content/60">
          Inventory / Report / Item List Summary
        </p>
      </div>

      <Card.Body className="gap-0 px-4 py-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="md:col-span-2 xl:col-span-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <Select
                label="Range"
                placeholder="Select range"
                className="w-full"
                options={[...RANGE_MODE_OPTIONS]}
                value={optionByValue(RANGE_MODE_OPTIONS, filter.rangeMode)}
                onChange={(v) => {
                  if (!v) return
                  setFilter((p) => ({
                    ...p,
                    rangeMode: v.value as ListItemSummaryFilter["rangeMode"]
                  }))
                }}
              />
            </div>
            <label className="form-control w-full min-w-0 sm:max-w-[220px]">
              <div className="label min-h-0 py-0 pb-1">
                <span className="label-text font-bold">Period</span>
              </div>
              <input
                type="month"
                className="input input-bordered w-full"
                value={filter.periodMonth}
                onChange={(e) =>
                  setFilter((p) => ({ ...p, periodMonth: e.target.value }))
                }
              />
            </label>
          </div>
        </div>

        <Select
          label="Location"
          placeholder="Select location"
          className="w-full"
          options={LOCATION_OPTIONS}
          value={optionByValue(LOCATION_OPTIONS, filter.location)}
          onChange={(v) => {
            if (!v) return
            setFilter((p) => ({ ...p, location: v.value }))
          }}
        />

        <Select
          label="Product Category"
          placeholder="Select category"
          className="w-full"
          options={CATEGORY_OPTIONS}
          value={optionByValue(CATEGORY_OPTIONS, filter.category)}
          onChange={(v) => {
            if (!v) return
            setFilter((p) => ({ ...p, category: v.value }))
          }}
        />

        <Select
          label="Stock Status"
          placeholder="Select status"
          className="w-full"
          options={STOCK_STATUS_OPTIONS}
          value={optionByValue(STOCK_STATUS_OPTIONS, filter.stockStatus)}
          onChange={(v) => {
            if (!v) return
            setFilter((p) => ({ ...p, stockStatus: v.value }))
          }}
        />

        <Select
          label="Inventory Value Range"
          placeholder="Select range"
          className="w-full"
          options={VALUE_RANGE_OPTIONS}
          value={optionByValue(VALUE_RANGE_OPTIONS, filter.valueRange)}
          onChange={(v) => {
            if (!v) return
            setFilter((p) => ({ ...p, valueRange: v.value }))
          }}
        />

        <Select
          label="Movement Status"
          placeholder="Select movement"
          className="w-full"
          options={MOVEMENT_OPTIONS}
          value={optionByValue(MOVEMENT_OPTIONS, filter.movement)}
          onChange={(v) => {
            if (!v) return
            setFilter((p) => ({ ...p, movement: v.value }))
          }}
        />

        <Select
          label="Supplier / Vendor"
          placeholder="Select supplier"
          className="w-full"
          options={SUPPLIER_OPTIONS}
          value={optionByValue(SUPPLIER_OPTIONS, filter.supplier)}
          onChange={(v) => {
            if (!v) return
            setFilter((p) => ({ ...p, supplier: v.value }))
          }}
        />

        <Input
          label="Search item"
          className="w-full"
          placeholder="Code, item name, category..."
          value={filter.search}
          onChange={(e) =>
            setFilter((p) => ({ ...p, search: e.target.value }))
          }
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-[#dadada] pt-4">
        <Button
          type="button"
          color="primary"
          size="sm"
          onClick={onSearchReport}>
          Search Report
        </Button>
        <Button
          type="button"
          color="outline-secondary"
          size="sm"
          onClick={onResetFilter}>
          Reset Filter
        </Button>
        <Button
          type="button"
          color="outline-secondary"
          size="sm"
          className="cursor-not-allowed opacity-50"
          disabled
          title="Nonaktif (placeholder)">
          Create Item
        </Button>
        <Button
          type="button"
          color="outline-secondary"
          size="sm"
          onClick={onLatestItems}>
          Latest Item
        </Button>
      </div>
      </Card.Body>
    </Card>
  )
}
